<template>
    <SplideSlide
        class="GalleryHero__splide-slide"
        :class="{
            'GalleryHero__splide-slide--ready':
                wordsReady && !loadingScreenActive,
            'GalleryHero__splide-slide--force-in': forceAnimateIn,
            'GalleryHero__splide-slide--first-slide': index === 0,
        }"
    >
        <component
            :is="item.to || item.href ? CustomLink : 'div'"
            :to="item.to"
            :href="item.href"
            class="GalleryHero__link-wrapper"
            @mouseover="toggleCursorOn"
            @mouseleave="toggleCursorOff"
        >
            <div class="GalleryHero__image-wrapper">
                <SmartImage
                    class="GalleryHero__image"
                    :url="item.image.url"
                    :srcs="[
                        {
                            slotWidth: item.image.dimensions.width,
                            width: item.image.dimensions.width,
                        },
                    ]"
                    :width="item.image.dimensions.width"
                    :height="item.image.dimensions.height"
                    :as-bg="true"
                    :lazy-load="index !== 0"
                />
            </div>
            <div class="GalleryHero__content">
                <SmartImage
                    v-if="item.logo?.url"
                    class="GalleryHero__logo"
                    :with-placeholder="false"
                    :url="item.logo.url"
                    :srcs="[
                        {
                            slotWidth: item.logo.dimensions.width,
                            width: item.logo.dimensions.width,
                        },
                    ]"
                    :width="item.logo.dimensions.width"
                    :height="item.logo.dimensions.height"
                />
                <h2 class="GalleryHero__content-title">
                    <div class="GalleryHero__content-title-link">
                        <span v-html="transformedTitle" />
                    </div>
                </h2>
                <div class="GalleryHero__tags">
                    <div
                        v-for="(tag, tagIndex) in item.tags"
                        class="GalleryHero__tag-wrapper"
                    >
                        <div
                            class="GalleryHero__tag"
                            :style="{ '--index': tagIndex }"
                        >
                            <CustomTag :text="tag" />
                        </div>
                    </div>
                </div>
            </div>
        </component>
    </SplideSlide>
</template>

<script setup lang="ts">
import { SplideSlide } from '@splidejs/vue-splide'
import { CustomLink } from '#components'

interface PrismicImage {
    url: string
    dimensions: {
        width: number
        height: number
    }
}

interface GalleryHeroItem {
    image: PrismicImage
    title: string
    logo: PrismicImage
    tags: Array<string>
    galleryShortCutText: string
    to: string
    href: string
}

interface GalleryHeroSlideProps {
    item: GalleryHeroItem
    index: number
    removeForceInForSlide: boolean
}

const props = defineProps<GalleryHeroSlideProps>()

const { $wordBuilder, hook } = useNuxtApp()
const cursorStore = useCursorStore()
const transformedTitle = ref('')
const titleEndIndex = ref(0)
const endWordIndex = ref(0)
const wordsReady = ref(false)
const loadingScreenActive = ref(true)
const forceAnimateIn = ref(false)

if (props.item.title) {
    $wordBuilder(props.item.title).then((res) => {
        transformedTitle.value = res.words
        titleEndIndex.value = res.endIndex
        endWordIndex.value = res.endIndex
        wordsReady.value = true
    })
}

hook('page:loading:end', () => {
    setTimeout(() => {
        loadingScreenActive.value = false

        if (props.index === 0) {
            forceAnimateIn.value = true
        }
    }, 750)
})

watch(
    () => props.removeForceInForSlide,
    (newValue) => {
        if (newValue) {
            forceAnimateIn.value = false
        }
    },
)

function toggleCursorOn() {
    cursorStore.setCursorText('View <br/> Project')
    cursorStore.setCursorType('view')
}

function toggleCursorOff() {
    cursorStore.setCursorText('')
    cursorStore.setCursorType('')
}
</script>

<style lang="scss">
.GalleryHero__splide-slide {
    position: relative;
}

.GalleryHero__splide-slide,
.GalleryHero__link-wrapper {
    @include grid;
}

.GalleryHero__link-wrapper {
    grid-column: 1 / span 24;
    text-decoration: none;
}

.GalleryHero__splide-slide--force-in,
.GalleryHero__splide-slide--ready.is-visible {
    .word span {
        animation: text-reveal 0.8s forwards cubic-bezier(0.25, 1, 0.5, 1)
            var(--transition-delay);
    }

    .GalleryHero__logo {
        img {
            transform: translateY(0%);
        }
    }

    .GalleryHero__tag {
        transform: translateX(0);
        opacity: 1;
    }
}

.GalleryHero__splide-slide--ready.GalleryHero__splide-slide--first-slide,
.GalleryHero__splide-slide--force-in.GalleryHero__splide-slide--ready.GalleryHero__splide-slide--first-slide {
    .GalleryHero__image {
        transform: scale(1);
        filter: grayscale(0);
    }
}

.GalleryHero__splide-slide--first-slide {
    .GalleryHero__image {
        transform: scale(1.08);
        filter: grayscale(1);
        transition: all 1s ease;
    }
}

.GalleryHero__image {
    width: 100%;
    height: 100%;
}

.GalleryHero__image-wrapper {
    position: absolute;
    width: 100%;
    height: 100%;

    &::after {
        content: '';
        position: absolute;
        z-index: 1;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: radial-gradient(
            63.26% 63.24% at 32.29% 46.42%,
            rgba(0, 0, 0, 0.8) 0%,
            rgba(0, 0, 0, 0) 100%
        );
    }
}

.GalleryHero__content {
    position: relative;
    z-index: 1;
    max-width: 46.875rem;
    grid-column: 2 / span 22;
    align-self: center;
}

.GalleryHero__logo {
    width: 8.75rem;
    margin-bottom: 0.5rem;
    overflow: hidden;

    img {
        transform: translateY(100%);
        transition: transform 1s cubic-bezier(0.25, 1, 0.5, 1);
    }
}

.GalleryHero__content-title {
    @include heading-2;
}

.GalleryHero__content-title-link {
    text-decoration: none;

    .word {
        overflow: hidden;
        display: inline-flex;
        line-height: 1;

        span {
            display: block;
            opacity: 1;
            transform: translate3d(0, 100%, 0);
            padding-right: 0.09em; // offset italic font edging out on the right
            --transition-delay: calc(0.25s + var(--index) * 0.05s);

            @include large-up {
                padding-right: 0.05em; // offset italic font edging out on the right
            }
        }
    }
}

.GalleryHero__tags {
    display: flex;
    flex-wrap: wrap;
    gap: 0.75rem;
    margin-top: 1rem;
}

.GalleryHero__tag {
    transform: translateX(-32px);
    opacity: 0;
    transition: all 1s calc(0.4s + var(--index) * 0.08s)
        cubic-bezier(0.25, 1, 0.5, 1);
}
</style>
